import { HOME_PAGE } from '@/constants/urls.const';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  PublicClientApplication,
  SilentRequest,
  RedirectRequest,
  EndSessionRequest,
  LogLevel,
  EventType,
  BrowserCacheLocation,
} from '@azure/msal-browser';
import { ref } from 'vue';

type RedirectResult = AuthenticationResult | null;

interface IMsalInstance {
  initialize: () => Promise<void>;
  getAllAccounts: () => AccountInfo[];
  loginRedirect: (request?: RedirectRequest) => Promise<void>;
  logoutRedirect: (request?: EndSessionRequest) => Promise<void>;
  acquireTokenSilent: (request: SilentRequest) => Promise<AuthenticationResult>;
  handleRedirectPromise: () => Promise<RedirectResult>;
  clearCache: () => Promise<void>;
}

const accounts = ref<AccountInfo[]>([]);
const inProgress = ref<string>('none');

const noopInstance: IMsalInstance = {
  initialize: async () => {},
  getAllAccounts: () => [],
  loginRedirect: async () => {},
  logoutRedirect: async () => {},
  acquireTokenSilent: async () => {
    throw new Error('MSAL is unavailable outside of browser runtime');
  },
  handleRedirectPromise: async () => null,
  clearCache: async () => {},
};

let instance: IMsalInstance | PublicClientApplication = noopInstance;
let initializePromise: Promise<void> | null = null;
let callbacksRegistered = false;

if (typeof window !== 'undefined') {
  const redirectUri = `${window.location.origin}${(process.env.VUE_APP_ENTRA_CALLBACK ?? '') as string}`;
  const msalClient = new PublicClientApplication({
    // Persist auth state across tabs/browser restarts.
    auth: {
      clientId: process.env.VUE_APP_ENTRA_CLIENT_ID as string,
      authority: `https://${process.env.VUE_APP_ENTRA_CUSTOM_HOST as string}/${process.env.VUE_APP_ENTRA_TENANT_ID as string}`,
      knownAuthorities: [process.env.VUE_APP_ENTRA_CUSTOM_HOST!],
      redirectUri,
      postLogoutRedirectUri: HOME_PAGE,
      // Keep the browser on callback route so handleRedirectPromise can complete, redirection is performed by code at a later step.
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
          if (containsPii) return;
          if (level === LogLevel.Error) console.error(message);
        },
        logLevel: LogLevel.Error,
      },
    },
  });

  instance = msalClient;
}

function registerCallbacks() {
  if (typeof window === 'undefined' || callbacksRegistered) return;

  const msalInstance = instance as PublicClientApplication;

  msalInstance.addEventCallback((event: EventMessage) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.LOGIN_FAILURE ||
      event.eventType === EventType.LOGOUT_SUCCESS ||
      event.eventType === EventType.HANDLE_REDIRECT_END
    ) {
      accounts.value = msalInstance.getAllAccounts();
      inProgress.value = 'none';
    }

    if (event.eventType === EventType.LOGIN_START || event.eventType === EventType.HANDLE_REDIRECT_START) {
      inProgress.value = 'interaction';
    }
  });

  callbacksRegistered = true;
}

export async function initializeMsal(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (initializePromise) {
    await initializePromise;
    return;
  }

  initializePromise = (async () => {
    await instance.initialize();

    registerCallbacks();
    // handleRedirectPromise clears any stale interaction_in_progress state left
    // in sessionStorage by an aborted redirect (e.g. user pressed Back).
    await instance.handleRedirectPromise();

    accounts.value = instance.getAllAccounts();
  })().catch((error: unknown) => {
    initializePromise = null;
    throw error;
  });

  await initializePromise;
}

export function useMsal() {
  // Keep initialization lazy for test and Storybook environments.
  if (typeof window !== 'undefined') {
    void initializeMsal();
  }

  return {
    instance,
    accounts,
    inProgress,
  };
}
